/* Publish a dataset publicly: slug, write-up, tags, license, linked analyses. */
import { fetchGraphEndpoint } from './utils/graph-request.js';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,58}[a-z0-9]$/;
const STYLE_ID = 'dataset-publish-style';

const ui = { dialog: null, button: null, ds: null, linkable: [], licenses: [], busy: false };

function getSecret() {
  try {
    return (window.userData && window.userData.secret) || window.localStorage.getItem('secret') || '';
  } catch {
    return (window.userData && window.userData.secret) || '';
  }
}

async function api(path, method = 'GET', body) {
  const secret = getSecret();
  if (!secret) throw new Error('Sign in to publish datasets.');
  const headers = { Accept: 'application/json', Authorization: `Bearer ${secret}` };
  const options = { method, credentials: 'include', headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }
  const response = await fetchGraphEndpoint(path, options);
  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }
  if (!response.ok || payload.success === false) {
    throw new Error(payload.error || payload.detail || `Request failed (${response.status})`);
  }
  return payload;
}

function el(tag, props = {}, children = []) {
  const node = document.createElement(tag);
  Object.entries(props).forEach(([key, value]) => {
    if (key === 'text') node.textContent = value;
    else if (key === 'class') node.className = value;
    else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
    else node.setAttribute(key, value);
  });
  children.forEach(child => node.appendChild(child));
  return node;
}

function injectStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #dataset-publish-dialog{width:min(720px,94vw);max-height:90vh;overflow:auto;padding:20px;border:1px solid #d0d5db}
    #dataset-publish-dialog label{display:block;margin:10px 0 4px;font-weight:600}
    #dataset-publish-dialog input[type=text],#dataset-publish-dialog textarea,#dataset-publish-dialog select{width:100%;box-sizing:border-box;padding:6px 8px}
    #dataset-publish-dialog .dp-row{display:flex;gap:12px;align-items:center;flex-wrap:wrap}
    #dataset-publish-dialog .dp-split{display:grid;grid-template-columns:1fr 1fr;gap:12px}
    #dataset-publish-dialog .dp-preview{border:1px solid #e5e7eb;padding:8px;min-height:120px;overflow:auto}
    #dataset-publish-dialog .dp-links li{display:flex;gap:8px;align-items:center;margin:4px 0;list-style:none}
    #dataset-publish-dialog .dp-links{padding:0;max-height:200px;overflow:auto}
    #dataset-publish-dialog .dp-status{min-height:1.4em;margin-top:8px}
    #dataset-publish-dialog .dp-error{color:#b91c1c}
  `;
  document.head.appendChild(style);
}

function setStatus(message, isError = false) {
  const node = ui.dialog && ui.dialog.querySelector('.dp-status');
  if (!node) return;
  node.textContent = message || '';
  node.className = `dp-status${isError ? ' dp-error' : ''}`;
}

function slugify(text) {
  return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

function field(id) {
  return ui.dialog.querySelector(`#dp-${id}`);
}

let previewTimer = null;
function schedulePreview() {
  clearTimeout(previewTimer);
  previewTimer = setTimeout(async () => {
    const target = ui.dialog && ui.dialog.querySelector('.dp-preview');
    if (!target) return;
    try {
      const result = await api('/api/datasets/preview-markdown', 'POST', { description: field('description').value });
      target.innerHTML = result.html;
    } catch (error) {
      target.textContent = error.message;
    }
  }, 300);
}

function buildDialog() {
  injectStyle();
  const dialog = el('dialog', { id: 'dataset-publish-dialog' });
  dialog.innerHTML = '';
  dialog.appendChild(el('h2', { text: 'Publish dataset' }));
  dialog.appendChild(el('div', { class: 'dp-row' }, [
    el('label', {}, [el('input', { type: 'checkbox', id: 'dp-public' }), document.createTextNode(' Public')]),
    el('span', { class: 'dp-link' })
  ]));
  dialog.appendChild(el('label', { for: 'dp-title', text: 'Title' }));
  dialog.appendChild(el('input', { type: 'text', id: 'dp-title', maxlength: '120' }));
  dialog.appendChild(el('label', { for: 'dp-slug', text: 'URL slug (/d/slug)' }));
  dialog.appendChild(el('input', { type: 'text', id: 'dp-slug', maxlength: '60', placeholder: 'auto from title' }));
  dialog.appendChild(el('label', { for: 'dp-description', text: 'Description (headings, bold, italic, links, lists, code)' }));
  dialog.appendChild(el('div', { class: 'dp-split' }, [
    el('textarea', { id: 'dp-description', rows: '10', maxlength: '20000', oninput: schedulePreview }),
    el('div', { class: 'dp-preview', 'aria-label': 'Description preview' })
  ]));
  dialog.appendChild(el('label', { for: 'dp-tags', text: 'Tags (comma separated, max 10)' }));
  dialog.appendChild(el('input', { type: 'text', id: 'dp-tags' }));
  dialog.appendChild(el('label', { for: 'dp-license', text: 'License' }));
  dialog.appendChild(el('select', { id: 'dp-license' }));
  dialog.appendChild(el('label', {}, [
    el('input', { type: 'checkbox', id: 'dp-download' }),
    document.createTextNode(' Allow CSV download')
  ]));
  dialog.appendChild(el('label', { text: 'Linked analyses (shared publicly when linked)' }));
  dialog.appendChild(el('ul', { class: 'dp-links', id: 'dp-links' }));
  dialog.appendChild(el('label', {}, [
    el('input', { type: 'checkbox', id: 'dp-confirm' }),
    document.createTextNode(' I understand this dataset preview and linked analyses will be visible to everyone')
  ]));
  dialog.appendChild(el('div', { class: 'dp-status', role: 'status' }));
  dialog.appendChild(el('div', { class: 'dp-row' }, [
    el('button', { type: 'button', class: 'mdl-button mdl-button--raised', id: 'dp-save', onclick: save, text: 'Save' }),
    el('button', { type: 'button', class: 'mdl-button', id: 'dp-copy', onclick: copyLink, text: 'Copy link' }),
    el('button', { type: 'button', class: 'mdl-button', onclick: () => dialog.close(), text: 'Close' })
  ]));
  document.body.appendChild(dialog);
  return dialog;
}

function renderLinkables(links) {
  const list = field('links');
  list.innerHTML = '';
  const linked = new Map(links.map(link => [`${link.item_type}:${link.item_id}`, link]));
  if (!ui.linkable.length) {
    list.appendChild(el('li', { text: 'No graphs or dashboards yet.' }));
    return;
  }
  ui.linkable.forEach(item => {
    const key = `${item.item_type}:${item.item_id}`;
    const existing = linked.get(key);
    const checkbox = el('input', { type: 'checkbox', 'data-key': key });
    checkbox.checked = Boolean(existing);
    const caption = el('input', { type: 'text', maxlength: '300', placeholder: 'Caption', 'data-caption': key });
    caption.value = existing ? existing.caption || '' : '';
    list.appendChild(el('li', {}, [
      checkbox,
      el('span', { text: `${item.item_type}: ${item.title}${item.is_public ? '' : ' (will be made public)'}` }),
      caption
    ]));
  });
}

function applyDataset(dataset) {
  ui.ds = dataset;
  field('public').checked = Boolean(dataset.is_public);
  field('title').value = dataset.title || dataset.name || '';
  field('slug').value = dataset.slug || '';
  field('description').value = dataset.description || '';
  field('tags').value = (dataset.tags || []).join(', ');
  field('license').value = dataset.license || '';
  field('download').checked = Boolean(dataset.allow_download);
  field('confirm').checked = false;
  const link = ui.dialog.querySelector('.dp-link');
  link.textContent = dataset.url || '';
  renderLinkables(dataset.links || []);
  schedulePreview();
}

async function copyLink() {
  const url = ui.ds && ui.ds.url;
  if (!url) {
    setStatus('Publish first to get a link.', true);
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    setStatus('Link copied.');
  } catch {
    setStatus(url);
  }
}

async function syncLinks() {
  const messages = [];
  const current = new Map((ui.ds.links || []).map(link => [`${link.item_type}:${link.item_id}`, link]));
  const rows = Array.from(ui.dialog.querySelectorAll('#dp-links input[type=checkbox]'));
  let position = 0;
  for (const box of rows) {
    const key = box.getAttribute('data-key');
    const [item_type, ...rest] = key.split(':');
    const item_id = rest.join(':');
    const caption = ui.dialog.querySelector(`[data-caption="${CSS.escape(key)}"]`).value;
    const base = `/api/datasets/${encodeURIComponent(ui.ds.id)}/links`;
    if (box.checked) {
      const result = await api(base, 'POST', { item_type, item_id, caption, position });
      position += 1;
      if (result.message) messages.push(result.message);
    } else if (current.has(key)) {
      await api(base, 'DELETE', { item_type, item_id });
    }
  }
  return messages;
}

async function save() {
  if (ui.busy || !ui.ds) return;
  ui.busy = true;
  setStatus('Saving…');
  try {
    const base = `/api/datasets/${encodeURIComponent(ui.ds.id)}`;
    let messages = [];
    if (field('public').checked) {
      const slug = field('slug').value.trim();
      if (slug && !SLUG_RE.test(slug)) throw new Error('Slug: 3-60 chars, lowercase letters, digits, hyphens.');
      if (!field('confirm').checked) throw new Error('Confirm that the dataset will be public.');
      await api(`${base}/publish`, 'PUT', {
        confirm_public: true,
        title: field('title').value.trim() || ui.ds.name,
        slug: slug || slugify(field('title').value) || undefined,
        description: field('description').value,
        tags: field('tags').value.split(',').map(tag => tag.trim()).filter(Boolean),
        license: field('license').value,
        allow_download: field('download').checked
      });
      const fresh = await api(base);
      ui.ds = fresh.dataset;
      messages = await syncLinks();
    } else if (ui.ds.is_public) {
      await api(`${base}/publish`, 'DELETE');
    }
    const refreshed = await api(base);
    applyDataset(refreshed.dataset);
    setStatus(['Saved.', ...messages].join(' '));
  } catch (error) {
    setStatus(error.message, true);
  } finally {
    ui.busy = false;
  }
}

async function openPanel(filename) {
  if (!ui.dialog) ui.dialog = buildDialog();
  setStatus('Loading…');
  ui.dialog.showModal();
  try {
    const mine = await api(`/api/datasets/mine?file=${encodeURIComponent(filename)}`);
    const match = (mine.datasets || [])[0];
    if (!match) throw new Error('Only datasets you uploaded can be published.');
    const [detail, linkable] = await Promise.all([
      api(`/api/datasets/${encodeURIComponent(match.id)}`),
      api(`/api/datasets/${encodeURIComponent(match.id)}/linkable`)
    ]);
    ui.linkable = linkable.items || [];
    ui.licenses = detail.licenses || [];
    const select = field('license');
    select.innerHTML = '';
    ui.licenses.forEach(item => select.appendChild(el('option', { value: item.id, text: item.label })));
    applyDataset(detail.dataset);
    setStatus('');
  } catch (error) {
    setStatus(error.message, true);
  }
}

function ensureButton(filename) {
  const form = document.getElementById('editdataaskfelixform');
  if (!form || !form.parentElement) return;
  if (!ui.button) {
    ui.button = el('button', {
      type: 'button',
      id: 'dataset-publish-button',
      class: 'mdl-button mdl-js-button',
      text: 'Publish'
    });
    form.parentElement.insertBefore(ui.button, form);
  }
  ui.button.hidden = false;
  ui.button.onclick = () => openPanel(filename);
}

document.addEventListener('dataset-editor:opened', event => {
  const filename = event && event.detail && event.detail.filename;
  if (filename) ensureButton(filename);
  else if (ui.button) ui.button.hidden = true;
});

if (typeof window !== 'undefined') {
  window.DatasetPublish = { open: openPanel };
}

export { openPanel };
